/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ListChecks, Plus } from 'lucide-react';
import { serviceRequestService } from '../../services/serviceRequestService';
import toastService from '../../utils/toastService';
import {
  SERVICE_REQUEST_CHECKLIST_TITLE_MAX_LENGTH,
  SERVICE_REQUEST_MAX_CHECKLISTS,
  SERVICE_REQUEST_CHECKLIST_SECTION_LABEL,
  SERVICE_REQUEST_CHECKLIST_SECTION_LABEL_LOWER,
} from '../../constants/serviceRequests';
import { SERVICE_REQUEST_LABEL } from '../../constants/entityLabels';
import ChecklistCard from './ChecklistCard';
import ChecklistDeleteModal from './ChecklistDeleteModal';
import {
  apiErrorMessage,
  checklistPermissionsShape,
  NO_CHECKLIST_PERMISSIONS,
} from './checklistHelpers';

/**
 * Checklists tab body on the service request detail page. Standalone: it loads and
 * saves its own data and is not part of the edit service request form.
 * `onChanged` is called after writes that add a timeline event; `onCountChange`
 * reports the number of checklists for the tab badge. `permissions` comes from
 * buildChecklistPermissions and decides which actions are shown.
 */
const noop = () => {};

const ServiceRequestChecklists = ({
  serviceRequestUid,
  permissions = NO_CHECKLIST_PERMISSIONS,
  onChanged = noop,
  onCountChange = noop,
}) => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [titleDraft, setTitleDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadChecklists = useCallback(async () => {
    try {
      const { data } = await serviceRequestService.getChecklists(serviceRequestUid);
      setChecklists(data || []);
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to load checklists'));
    } finally {
      setLoading(false);
    }
  }, [serviceRequestUid]);

  useEffect(() => {
    setLoading(true);
    loadChecklists();
  }, [loadChecklists]);

  useEffect(() => {
    onCountChange(checklists.length);
  }, [checklists.length, onCountChange]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const title = titleDraft.trim();
    if (!title) return;
    try {
      setCreating(true);
      const { data } = await serviceRequestService.createChecklist(serviceRequestUid, title);
      setChecklists((prev) => [...prev, data]);
      setTitleDraft('');
      toastService.success('Checklist created successfully');
      onChanged();
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to create checklist'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await serviceRequestService.deleteChecklist(serviceRequestUid, deleteTarget.uid);
      setChecklists((prev) => prev.filter((c) => c.uid !== deleteTarget.uid));
      setDeleteTarget(null);
      toastService.success('Checklist deleted successfully');
      onChanged();
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to delete checklist'));
    } finally {
      setDeleting(false);
    }
  };

  const updateChecklist = (checklistUid) => (updater) =>
    setChecklists((prev) => prev.map((c) => (c.uid === checklistUid ? updater(c) : c)));

  return (
    <div>
      {permissions.create && !loading && checklists.length < SERVICE_REQUEST_MAX_CHECKLISTS && (
        <form onSubmit={handleCreate} className="flex items-center gap-2 mb-4">
          <input
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            maxLength={SERVICE_REQUEST_CHECKLIST_TITLE_MAX_LENGTH}
            placeholder="New checklist title..."
            disabled={creating}
            className="input input-sm input-bordered flex-1 min-w-0 rounded-xl text-sm bg-base-200/40 focus:outline-none focus:border-primary"
          />
          <button type="submit" disabled={creating || !titleDraft.trim()} className="btn btn-sm btn-primary rounded-xl gap-2 font-bold">
            {creating ? <span className="loading loading-spinner loading-xs"></span> : <Plus size={14} />}
            Add Checklist
          </button>
        </form>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-24 bg-base-200/60 rounded-xl animate-pulse"></div>
          ))}
        </div>
      ) : checklists.length === 0 ? (
        <div className="py-10 flex flex-col items-center justify-center text-center space-y-2">
          <div className="w-12 h-12 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30">
            <ListChecks size={24} />
          </div>
          <p className="text-sm font-bold text-base-content/60">No {SERVICE_REQUEST_CHECKLIST_SECTION_LABEL_LOWER} yet</p>
          <p className="text-xs text-base-content/40 max-w-xs">
            {SERVICE_REQUEST_CHECKLIST_SECTION_LABEL} added to this {SERVICE_REQUEST_LABEL.singularLower} will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {checklists.map((checklist) => (
            <ChecklistCard
              key={checklist.uid}
              checklist={checklist}
              serviceRequestUid={serviceRequestUid}
              permissions={permissions}
              onUpdate={updateChecklist(checklist.uid)}
              onDelete={setDeleteTarget}
              onReload={loadChecklists}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}

      <ChecklistDeleteModal
        isOpen={!!deleteTarget}
        title="Delete Checklist"
        message="Are you sure you want to delete this checklist?"
        preview={deleteTarget?.title}
        note="All of its items and their comments will be deleted too."
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

ServiceRequestChecklists.propTypes = {
  serviceRequestUid: PropTypes.string.isRequired,
  permissions: checklistPermissionsShape,
  onChanged: PropTypes.func,
  onCountChange: PropTypes.func,
};

export default ServiceRequestChecklists;
